import { Label } from "@volca/ui/components/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@volca/ui/components/ui/select";
import { Spinner } from "@volca/ui/components/ui/spinner";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { useGetQuickbooksAccounts } from "@/business/quickbooks-query";
import { quickbooksAccountLabel } from "@/business/quickbooks-utils";

const FIELD_ID = "quickbooks-default-expense-account";

const LoadingLine = () => (
  <Stack direction="row" gap={2} align="center" role="status">
    <Spinner layout="centered" size="sm" />
    <Text tone="muted">Loading your QuickBooks accounts...</Text>
  </Stack>
);

/**
 * The business's default expense account. While the account mirror is still
 * syncing after a connect, the list isn't asked for: the connection poll says
 * when accounts have arrived.
 */
export const QuickbooksDefaultAccountField = ({
  businessId,
  isSyncing,
  value,
  onChange,
  disabled,
}: {
  businessId: string;
  isSyncing: boolean;
  value: string | null;
  onChange: (accountId: string) => void;
  disabled?: boolean;
}) => {
  const {
    data: accounts,
    isPending,
    isError,
  } = useGetQuickbooksAccounts(businessId, { enabled: !isSyncing });

  const renderControl = () => {
    if (isSyncing || isPending) return <LoadingLine />;
    if (isError) {
      return (
        <Text tone="destructive" role="alert">
          Couldn’t load your QuickBooks accounts. Please try again.
        </Text>
      );
    }
    if (accounts.length === 0) {
      return (
        <Text tone="muted">
          No expense accounts found in QuickBooks. Add one there, then reopen
          bill settings.
        </Text>
      );
    }
    return (
      <Select
        value={value}
        items={accounts.map((account) => ({
          value: account.id,
          label: quickbooksAccountLabel(account),
        }))}
        onValueChange={(next) => {
          if (next) onChange(next);
        }}
        disabled={disabled}
      >
        <SelectTrigger id={FIELD_ID} className="w-full">
          <SelectValue placeholder="Choose an account" />
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            {accounts.map((account) => (
              <SelectItem key={account.id} value={account.id}>
                {quickbooksAccountLabel(account)}
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
    );
  };

  return (
    <Stack className="w-full" align="start" gap={2}>
      <Label htmlFor={FIELD_ID}>Default expense account</Label>
      {renderControl()}
      <Text variant="caption" tone="muted">
        Bills are filed to this account unless a vendor has its own.
      </Text>
    </Stack>
  );
};
